import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

import * as approvalV2Api from '../../lib/api/approvalV2';
import ApprovalV2HistoryPanel from '../../components/approval/ApprovalV2HistoryPanel';
import ApprovalV2ActionDialog from '../../components/approval/ApprovalV2ActionDialog';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';

const DOC_TYPE_LABELS = { ORDER_BOOK: '수주품의서', HR_LEAVE: '휴가', TAX_BILL_ISSUE: '세금계산서 발행 요청' };
const DOC_STATUS_LABELS = {
  IN_PROGRESS: '진행중',
  APPROVED: '승인',
  REJECTED: '반려',
  RECALLED: '회수',
  CANCELED: '취소',
};
const DOC_STATUS_COLORS = {
  IN_PROGRESS: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  RECALLED: 'default',
  CANCELED: 'default',
};

/**
 * 결재함(V2) - 결재선 템플릿/전결로 상신된 문서(ORDER_BOOK/HR_LEAVE)를 보여준다.
 * ksm_frontend의 ApprovalInboxV2Container/ApprovalSubmittedV2Container를 한 화면으로 모았다
 * (2026-10-20 결재 V2 이관). bito 기존 결재함(V1, ApprovalListContainer)은 그대로 두고
 * 이 화면을 별도 탭으로 추가했다 - V1/V2 문서가 같은 테이블에 있지 않아 목록을 합치지
 * 않았다(KSM도 결재 모니터링은 V1만 보여준다 - 같은 제약).
 *
 * 승인/반려/회수는 범용 엔드포인트(POST .../documents/{apprDocId}/approve 등)로 처리한다 -
 * 문서종류와 무관하게 동작하고(ApprovalCompletionHandler가 뒷단에서 문서종류별 후처리를
 * 담당), 수주품의서든 휴가든 이 화면 하나로 처리할 수 있다.
 */
const ApprovalV2BoardContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState('inbox');
  const [docs, setDocs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [detailByDoc, setDetailByDoc] = useState({});
  const [actionDialog, setActionDialog] = useState(null); // { apprDocId, action, docTitle }

  const load = () => {
    const call = view === 'inbox' ? approvalV2Api.getApprovalV2Inbox({}) : approvalV2Api.getApprovalV2Submitted({});
    call.then((res) => setDocs(res?.data?.list || []));
  };

  useEffect(() => {
    load();
    setExpanded({});
  }, [view]);

  const toggleExpand = async (apprDocId) => {
    const next = { ...expanded, [apprDocId]: !expanded[apprDocId] };
    setExpanded(next);
    if (next[apprDocId] && !detailByDoc[apprDocId]) {
      const res = await approvalV2Api.getApprovalV2Document(apprDocId);
      setDetailByDoc((prev) => ({ ...prev, [apprDocId]: res?.data }));
    }
  };

  const refreshDetail = async (apprDocId) => {
    const res = await approvalV2Api.getApprovalV2Document(apprDocId);
    setDetailByDoc((prev) => ({ ...prev, [apprDocId]: res?.data }));
  };

  const handleAction = async (comment) => {
    const { apprDocId, action } = actionDialog;
    try {
      if (action === 'approve') {
        await approvalV2Api.approveApprovalV2Document(apprDocId, comment);
      } else if (action === 'reject') {
        await approvalV2Api.rejectApprovalV2Document(apprDocId, comment);
      } else if (action === 'recall') {
        await approvalV2Api.recallApprovalV2Document(apprDocId);
      }
      enqueueSnackbar('처리되었습니다.', { variant: 'success' });
      setActionDialog(null);
      load();
      refreshDetail(apprDocId);
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  return (
    <>
      <BreadcrumbsContainer title="결재관리/결재함(V2)" />
      <Paper sx={{ p: 2 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(e, v) => v && setView(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="inbox">결재함(내가 처리할 문서)</ToggleButton>
          <ToggleButton value="submitted">기안함(내가 상신한 문서)</ToggleButton>
        </ToggleButtonGroup>

        <Stack spacing={1}>
          {docs.map((doc) => (
            <Paper key={doc.apprDocId} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={DOC_TYPE_LABELS[doc.docType] || doc.docType} variant="outlined" />
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {/* ORDER_BOOK은 docTitle 자체에 이미 "(품의서 번호)"가 들어있어 뒤에 refDocNo를
                      무조건 또 붙이면 중복 표시된다 - docTitle에 이미 포함돼 있으면 생략
                      (2026-10-20 화면 점검, ApprovalV2MonitorContainer와 동일 수정). */}
                  {doc.refDocNo && doc.docTitle.includes(doc.refDocNo)
                    ? doc.docTitle
                    : `${doc.docTitle} (${doc.refDocNo || doc.refDocId})`}
                </Typography>
                <Chip
                  size="small"
                  label={DOC_STATUS_LABELS[doc.docStatus] || doc.docStatus}
                  color={DOC_STATUS_COLORS[doc.docStatus] || 'default'}
                />
                {/* 대결(위임) 안내(2026-10-21, 점검 이슈 #9) - 내 결재함 쿼리는 현재 단계의
                    CURRENT_ASSIGNEE_USR_UUID가 나인 문서만 보여주는데, 원래 결재자
                    (originalAssigneeUsrUuid)가 내가 아니면 대결(위임)로 나에게 넘어온 건이다.
                    원래 결재선 상 담당자(팀장 등)가 "왜 결재함에 안 보이지" 하고 당황하는
                    대신, 받은 사람(나)이 "누구 대신 처리 중인지" 바로 알 수 있게 한다. */}
                {view === 'inbox' && doc.originalAssigneeUsrUuid && doc.originalAssigneeUsrUuid !== doc.currentActorUsrUuid && (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`${doc.originalAssigneeNm || '원 결재자'}님 대신 결재`}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {doc.submittedAt ? dayjs(doc.submittedAt).format('YYYY-MM-DD HH:mm') : ''}
                </Typography>
                <IconButton size="small" onClick={() => toggleExpand(doc.apprDocId)}>
                  {expanded[doc.apprDocId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>

              {view === 'inbox' && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setActionDialog({ apprDocId: doc.apprDocId, action: 'approve', docTitle: doc.docTitle })}
                  >
                    승인
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setActionDialog({ apprDocId: doc.apprDocId, action: 'reject', docTitle: doc.docTitle })}
                  >
                    반려
                  </Button>
                </Stack>
              )}
              {view === 'submitted' && doc.docStatus === 'IN_PROGRESS' && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setActionDialog({ apprDocId: doc.apprDocId, action: 'recall', docTitle: doc.docTitle })}
                  >
                    회수
                  </Button>
                </Stack>
              )}

              <Collapse in={Boolean(expanded[doc.apprDocId])} unmountOnExit>
                <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                  {detailByDoc[doc.apprDocId] ? (
                    <ApprovalV2HistoryPanel
                      steps={detailByDoc[doc.apprDocId].steps || []}
                      actions={detailByDoc[doc.apprDocId].actions || []}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      불러오는 중...
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
          {docs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {view === 'inbox' ? '처리할 결재(V2) 문서가 없습니다.' : '상신한 결재(V2) 문서가 없습니다.'}
            </Typography>
          )}
        </Stack>
      </Paper>

      <ApprovalV2ActionDialog
        open={Boolean(actionDialog)}
        action={actionDialog?.action}
        docTitle={actionDialog?.docTitle}
        onClose={() => setActionDialog(null)}
        onConfirm={handleAction}
      />
    </>
  );
};

export default ApprovalV2BoardContainer;
